import {useBreakpointDown} from '@travel-frontend/core';
import {useCallback, useLayoutEffect, useRef, useState} from 'react';

import {useFilterParams} from '@/modules/common/hooks/useFilterParams';
import {useSearchParams} from '@/modules/common/hooks/useSearchParams';
import {useSortParams} from '@/modules/common/hooks/useSortParams';
import {useTracking} from '@/modules/common/hooks/useTracking';
import {useAppDispatch} from '@/redux/config';
import {fetchAllHotels} from '@/redux/v1/hotels/list/actions';

type RefFunction<T> = {
  current: null | T;
};

type SnapshotBeforeActivation = {
  pageScrollY: number | null;
  listElementOffset: number | null;
  filtersElementBottomDistance: number | null;
};

type UseMapViewProps = {
  listElement: RefFunction<HTMLDivElement>;
  filtersElement: RefFunction<HTMLDivElement>;
  searchBarHeight: number;
};

type UseMapViewReturnValue = {
  active: boolean;
  mode: 'overlay' | 'sticky';
  activate: () => void;
  deactivate: () => void;
};

export const useMapView = ({
  listElement,
  filtersElement,
  searchBarHeight,
}: UseMapViewProps): UseMapViewReturnValue => {
  const dispatch = useAppDispatch();
  const mdDown = useBreakpointDown('md');
  const [active, setActive] = useState(false);
  const {dispatchGenericTrackingEvent} = useTracking();
  const [searchParams] = useSearchParams();
  const [sortParams] = useSortParams();
  const [filterParams] = useFilterParams();

  const snapshotBeforeActivation = useRef<SnapshotBeforeActivation>({
    pageScrollY: null,
    listElementOffset: null,
    filtersElementBottomDistance: null,
  });
  const mode = mdDown ? 'overlay' : 'sticky';

  const getSnapshotBeforeActivation =
    useCallback((): SnapshotBeforeActivation => {
      const pageScrollY = window.scrollY;
      let listElementOffset = null;
      let filtersElementBottomDistance = null;

      if (listElement.current) {
        listElementOffset =
          listElement.current.getBoundingClientRect().top + pageScrollY;
      }

      if (document.documentElement && filtersElement.current) {
        filtersElementBottomDistance =
          document.documentElement.clientHeight -
          filtersElement.current.getBoundingClientRect().bottom;
      }

      return {
        pageScrollY,
        listElementOffset,
        filtersElementBottomDistance,
      };
    }, [listElement, filtersElement]);

  const activate = useCallback(() => {
    snapshotBeforeActivation.current = getSnapshotBeforeActivation();
    dispatch(
      fetchAllHotels({
        filterParams,
        searchParams,
        sortParams,
      })
    );
    setActive(true);
    dispatchGenericTrackingEvent({
      event: 'click',
      event_action: 'map_clicked',
      non_interaction: false,
    });
  }, [
    getSnapshotBeforeActivation,
    dispatch,
    filterParams,
    searchParams,
    sortParams,
    dispatchGenericTrackingEvent,
  ]);

  const deactivate = useCallback(() => {
    setActive(false);
    dispatchGenericTrackingEvent({
      event: 'click',
      event_action: 'back_to_hotellist_clicked',
      non_interaction: false,
    });
  }, [dispatchGenericTrackingEvent]);

  // scroll to top of the list when activating the map view AND having scrolled past the filters
  useLayoutEffect(() => {
    const {current: snapshot} = snapshotBeforeActivation;

    if (
      active &&
      mode === 'sticky' &&
      snapshot.listElementOffset != null &&
      snapshot.filtersElementBottomDistance != null
    ) {
      if (snapshot.filtersElementBottomDistance > 0) {
        window.scroll(0, snapshot.listElementOffset - searchBarHeight);
      }
      snapshot.filtersElementBottomDistance = null;
      snapshot.listElementOffset = null;
    }
  }, [mode, active, searchBarHeight]);

  // scroll restoration when deactivating the map view
  // @TODO only restore to scroll state before map view activation if no filters have been applied, otherwise scroll to top
  useLayoutEffect(() => {
    const {current: snapshot} = snapshotBeforeActivation;

    if (!active && snapshot.pageScrollY != null) {
      window.scroll(0, snapshot.pageScrollY);
      snapshot.pageScrollY = null;
    }
  }, [active]);

  return {
    active,
    mode,
    activate,
    deactivate,
  };
};
